import { useState } from "react";
import { api } from "../lib/utils";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import {
  FileText,
  CheckCircle,
  Clock,
  Send,
  Loader2,
  AlertCircle,
  MessageSquare,
} from "lucide-react";

export default function WrittenInterviewSection({ candidate, onRefresh }) {
  const [answers, setAnswers] = useState(() => {
    // Pre-fill with existing answers if any
    const existing = candidate.written_interview?.candidate_answers || [];
    const map = {};
    existing.forEach(a => { map[a.question_id] = a.answer_text || ""; });
    return map;
  });
  const [submitting, setSubmitting] = useState(false);
  const [clarificationAnswers, setClarificationAnswers] = useState({});
  const [submittingClarifications, setSubmittingClarifications] = useState(false);

  const wi = candidate.written_interview;
  const questions = candidate.interview_questions || [];
  const status = wi?.status;

  const handleSubmitAnswers = async () => {
    const unanswered = questions.filter(q => !answers[q.question_id]?.trim());
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} remaining questions`);
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = questions.map(q => ({
        question_id: q.question_id,
        answer_text: answers[q.question_id] || "",
      }));

      await api.post(`/candidates/${candidate.candidate_id}/written-answers`, {
        answers: formattedAnswers,
      });
      toast.success("Answers submitted! The external assessor will be notified.");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit answers");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitClarifications = async () => {
    const pendingClarifications = getPendingClarifications();
    const unanswered = pendingClarifications.filter(c => !clarificationAnswers[c.clarification_id]?.trim());
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${unanswered.length} clarification questions`);
      return;
    }

    setSubmittingClarifications(true);
    try {
      const formatted = pendingClarifications.map(c => ({
        clarification_id: c.clarification_id,
        answer_text: clarificationAnswers[c.clarification_id] || "",
      }));

      await api.post(`/candidates/${candidate.candidate_id}/clarification-answers`, {
        answers: formatted,
      });
      toast.success("Clarification answers submitted!");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit clarifications");
    } finally {
      setSubmittingClarifications(false);
    }
  };

  const getPendingClarifications = () => {
    const allClarifications = [];
    const scores = wi?.assessor_scores || [];
    scores.forEach(s => {
      (s.clarifications || []).forEach(c => {
        if (!c.answer_text) {
          const q = questions.find(q => q.question_id === s.question_id);
          allClarifications.push({
            ...c,
            question_id: s.question_id,
            original_question: q?.question_text || "",
            question_number: q?.question_number || 0,
          });
        }
      });
    });
    return allClarifications;
  };

  // Status: pending_answers — show answer form
  if (status === "pending_answers") {
    return (
      <Card className="border-purple-200 bg-purple-50" data-testid="written-interview-section">
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <FileText className="w-5 h-5" />
              Written Interview
            </CardTitle>
            <Badge className="bg-purple-600">Pending Your Answers</Badge>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
            <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Deadline: {wi.deadline}
            </p>
            <p className="text-xs text-amber-600 mt-1">Please submit all answers before this date.</p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Answer each question thoroughly with specific examples from your experience.
          </p>

          {questions.map((q, idx) => (
            <div key={q.question_id} className="p-4 bg-white rounded-lg border border-slate-200" data-testid={`written-q-${idx}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-7 h-7 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">
                  {idx + 1}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{q.question_text}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Related to: {q.competency_name}</p>
                </div>
              </div>
              <Textarea
                placeholder="Type your answer here..."
                value={answers[q.question_id] || ""}
                onChange={(e) => setAnswers({ ...answers, [q.question_id]: e.target.value })}
                rows={4}
                data-testid={`written-answer-${idx}`}
              />
            </div>
          ))}

          <div className="flex justify-end pt-4">
            <Button onClick={handleSubmitAnswers} disabled={submitting} data-testid="submit-written-answers-btn">
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Submit All Answers</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Status: clarification_pending — show clarification questions
  if (status === "clarification_pending") {
    const pending = getPendingClarifications();

    return (
      <Card className="border-amber-200 bg-amber-50" data-testid="written-interview-clarification">
        <CardHeader>
          <div className="flex items-start justify-between">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <MessageSquare className="w-5 h-5" />
              Clarification Needed
            </CardTitle>
            <Badge className="bg-amber-600">{pending.length} Pending</Badge>
          </div>
          <p className="text-sm text-amber-700 mt-2">
            The assessor needs more detail on some of your answers. Please respond below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {pending.map((c) => (
            <div key={c.clarification_id} className="p-4 bg-white rounded-lg border border-amber-200">
              <p className="text-xs text-slate-500 mb-1">Regarding Question #{c.question_number}</p>
              <p className="text-sm text-slate-600 italic mb-2">"{c.original_question}"</p>
              <div className="bg-amber-50 p-3 rounded-md mb-3">
                <p className="text-sm font-medium text-amber-800">
                  <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                  Assessor asks: {c.question_text}
                </p>
              </div>
              <Textarea
                placeholder="Your clarification response..."
                value={clarificationAnswers[c.clarification_id] || ""}
                onChange={(e) => setClarificationAnswers({ ...clarificationAnswers, [c.clarification_id]: e.target.value })}
                rows={3}
              />
            </div>
          ))}

          <div className="flex justify-end pt-4">
            <Button onClick={handleSubmitClarifications} disabled={submittingClarifications}>
              {submittingClarifications ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Submit Clarifications</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Status: answers_submitted or scoring — waiting state
  if (status === "answers_submitted" || status === "scoring") {
    return (
      <Card className="border-blue-200 bg-blue-50" data-testid="written-interview-waiting">
        <CardContent className="p-6 text-center">
          <Clock className="w-12 h-12 text-blue-400 mx-auto mb-3" />
          <h3 className="font-medium text-blue-800 mb-1">Answers Under Review</h3>
          <p className="text-sm text-blue-600">
            Your written interview answers have been submitted. The external assessor is reviewing them.
          </p>
          <p className="text-xs text-slate-500 mt-3">Submitted: {wi.candidate_submitted_at ? new Date(wi.candidate_submitted_at).toLocaleDateString() : "N/A"}</p>
        </CardContent>
      </Card>
    );
  }

  // Status: completed — show summary
  if (status === "completed") {
    return (
      <Card className={`border-${candidate.interview_results?.overall_result === "passed" ? "green" : "red"}-200`} data-testid="written-interview-completed">
        <CardContent className="p-6 text-center">
          <CheckCircle className={`w-12 h-12 mx-auto mb-3 ${candidate.interview_results?.overall_result === "passed" ? "text-green-500" : "text-red-500"}`} />
          <h3 className="font-medium text-slate-800 mb-1">Written Interview {candidate.interview_results?.overall_result === "passed" ? "Passed" : "Completed"}</h3>
          <p className="text-sm text-slate-600">
            {candidate.interview_results?.passed_count || 0} of {candidate.interview_results?.total_questions || 10} questions passed
          </p>
        </CardContent>
      </Card>
    );
  }

  return null;
}
